import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../services/api'

// ===== Thunks =====
export const fetchGrilla = createAsyncThunk(
  'cuadroListas/fetchGrilla',
  async ({ anio, mes } = {}, { rejectWithValue }) => {
    try {
      const qs = new URLSearchParams({ anio: anio || '', mes: mes || '' }).toString()
      const r = await api.get(`/cuadro-listas?${qs}`)
      return r.data.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error al cargar el cuadro de listas')
    }
  }
)

export const marcarCelda = createAsyncThunk(
  'cuadroListas/marcarCelda',
  async ({ origen, columnaId, bomberoId, estado, colKey }, { rejectWithValue }) => {
    try {
      await api.put('/cuadro-listas/celda', { origen, columnaId, bomberoId, estado })
      return { colKey, bomberoId, estado }
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'No se pudo marcar la casilla')
    }
  }
)

export const setCitacionMeta = createAsyncThunk(
  'cuadroListas/setCitacionMeta',
  async ({ citacionId, numeroServicio, colKey }, { rejectWithValue }) => {
    try {
      await api.put(`/cuadro-listas/citacion-meta/${citacionId}`, { numeroServicio })
      return { colKey, numeroServicio }
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'No se pudo guardar el N° de servicio')
    }
  }
)

export const fetchClaves = createAsyncThunk('cuadroListas/fetchClaves', async (_, { rejectWithValue }) => {
  try {
    const r = await api.get('/cuadro-listas/claves')
    return r.data.data
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Error al cargar claves')
  }
})
export const createClave = createAsyncThunk('cuadroListas/createClave', async (data, { rejectWithValue }) => {
  try { return (await api.post('/cuadro-listas/claves', data)).data.data }
  catch (e) { return rejectWithValue(e.response?.data?.message || 'Error al crear clave') }
})
export const updateClave = createAsyncThunk('cuadroListas/updateClave', async ({ id, data }, { rejectWithValue }) => {
  try { return (await api.put(`/cuadro-listas/claves/${id}`, data)).data.data }
  catch (e) { return rejectWithValue(e.response?.data?.message || 'Error al actualizar clave') }
})
export const deleteClave = createAsyncThunk('cuadroListas/deleteClave', async (id, { rejectWithValue }) => {
  try { await api.delete(`/cuadro-listas/claves/${id}`); return id }
  catch (e) { return rejectWithValue(e.response?.data?.message || 'Error al eliminar clave') }
})
export const cargarEmergencias = createAsyncThunk('cuadroListas/cargarEmergencias', async (_, { rejectWithValue }) => {
  try { const r = (await api.post('/cuadro-listas/claves/cargar-emergencias')).data; return { creadas: r.creadas || 0, claves: r.data } }
  catch (e) { return rejectWithValue(e.response?.data?.message || 'Error al cargar claves de emergencia') }
})

export const createEvento = createAsyncThunk('cuadroListas/createEvento', async (data, { rejectWithValue }) => {
  try { return (await api.post('/cuadro-listas/eventos', data)).data.data }
  catch (e) { return rejectWithValue(e.response?.data?.details || e.response?.data?.message || 'Error al crear evento') }
})
export const updateEvento = createAsyncThunk('cuadroListas/updateEvento', async ({ id, data }, { rejectWithValue }) => {
  try { return (await api.put(`/cuadro-listas/eventos/${id}`, data)).data.data }
  catch (e) { return rejectWithValue(e.response?.data?.details || e.response?.data?.message || 'Error al actualizar evento') }
})
export const deleteEvento = createAsyncThunk('cuadroListas/deleteEvento', async (id, { rejectWithValue }) => {
  try { await api.delete(`/cuadro-listas/eventos/${id}`); return id }
  catch (e) { return rejectWithValue(e.response?.data?.message || 'Error al eliminar evento') }
})

// Recalcula totales de un bombero y de las columnas tras un cambio de casilla
function recomputarTotales(grilla) {
  if (!grilla) return
  const totCol = {}
  // Debe reflejar el mismo cálculo que el server (construirGrilla): 5 marcas P/A/L/S/PA y
  // total = suma de las 5. Antes omitía PA y suspensiones → tras marcar una casilla el total
  // optimista divergía del que trae un refresh del server.
  for (const c of grilla.columnas) totCol[c.key] = { P: 0, A: 0, L: 0, S: 0, PA: 0 }
  for (const b of grilla.bomberos) {
    const t = { asistencias: 0, faltas: 0, licencias: 0, suspensiones: 0, pendientes: 0, total: 0 }
    for (const [k, est] of Object.entries(b.celdas || {})) {
      if (est === 'P') { t.asistencias++; totCol[k] && totCol[k].P++ }
      else if (est === 'A') { t.faltas++; totCol[k] && totCol[k].A++ }
      else if (est === 'L') { t.licencias++; totCol[k] && totCol[k].L++ }
      else if (est === 'S') { t.suspensiones++; totCol[k] && totCol[k].S++ }
      else if (est === 'PA') { t.pendientes++; totCol[k] && totCol[k].PA++ }
    }
    t.total = t.asistencias + t.faltas + t.licencias + t.suspensiones + t.pendientes
    b.totales = t
  }
  for (const c of grilla.columnas) c.totales = totCol[c.key]
}

const initialState = {
  grilla: null, // { periodo, columnas, bomberos }
  claves: [],
  loading: false,
  error: null,
  claveError: null,
}

const slice = createSlice({
  name: 'cuadroListas',
  initialState,
  reducers: {
    clearErrors: (s) => { s.error = null; s.claveError = null },
  },
  extraReducers: (b) => {
    b.addCase(fetchGrilla.pending, (s) => { s.loading = true; s.error = null })
      .addCase(fetchGrilla.fulfilled, (s, a) => { s.loading = false; s.grilla = a.payload })
      .addCase(fetchGrilla.rejected, (s, a) => { s.loading = false; s.error = a.payload })

      // marcado optimista: la casilla ya se aplica al confirmarse el server
      .addCase(marcarCelda.fulfilled, (s, a) => {
        const { colKey, bomberoId, estado } = a.payload
        const bom = s.grilla?.bomberos.find((x) => x.id === bomberoId)
        if (bom) {
          if (estado === null) delete bom.celdas[colKey]
          else bom.celdas[colKey] = estado
          recomputarTotales(s.grilla)
        }
      })
      // marcarCelda.rejected NO setea s.error: la página ya toastea el fallo (aplicarEstado),
      // y el Alert de s.error quedaba pegado sobre una grilla que funciona (doble feedback).

      .addCase(setCitacionMeta.fulfilled, (s, a) => {
        const col = s.grilla?.columnas.find((c) => c.key === a.payload.colKey)
        if (col) col.numeroServicio = a.payload.numeroServicio
      })

      .addCase(fetchClaves.fulfilled, (s, a) => { s.claves = a.payload })
      // Sin este handler, un fallo al cargar el catálogo de claves (Cuadro y selector de
      // clave del parte en Emergencias) quedaba silencioso; se expone vía claveError.
      .addCase(fetchClaves.rejected, (s, a) => { s.claveError = a.payload })
      .addCase(createClave.fulfilled, (s, a) => { s.claves.unshift(a.payload) })
      .addCase(createClave.rejected, (s, a) => { s.claveError = a.payload })
      .addCase(updateClave.fulfilled, (s, a) => {
        const i = s.claves.findIndex((c) => c.id === a.payload.id); if (i !== -1) s.claves[i] = a.payload
      })
      .addCase(updateClave.rejected, (s, a) => { s.claveError = a.payload })
      .addCase(deleteClave.fulfilled, (s, a) => { s.claves = s.claves.filter((c) => c.id !== a.payload) })
      .addCase(cargarEmergencias.fulfilled, (s, a) => { s.claves = a.payload.claves })
      .addCase(cargarEmergencias.rejected, (s, a) => { s.claveError = a.payload })

      // createEvento/updateEvento.rejected ya no setean un `eventoError` muerto: la página
      // (guardarEvento/guardarNumServicio) toastea el error del .unwrap() rechazado.
  },
})

export const { clearErrors } = slice.actions
export default slice.reducer
